// Journal d'entrainement : prevu (calendrier) et realise (activites lues dans
// intervals.icu) ; IntervalsIcuController.journal cote serveur.
import { avecAthlete, type Cible, type SeancePlanifiee } from '../Planning/planningApi.ts';

export type ActiviteRealisee = {
  id: string;
  lien: string;
  date: string; // AAAA-MM-JJ
  heure?: string | null;
  nom: string;
  type?: string | null;
  source?: string | null;
  distanceM?: number | null;
  dureeS?: number | null;
  dureeTotaleS?: number | null;
  deniveleM?: number | null;
  fcMoyenne?: number | null;
  fcMax?: number | null;
  charge?: number | null;
  rpe?: number | null;
  // Activite Strava : intervals.icu n'en donne pas le detail.
  incomplete: boolean;
  // Seance prevue realisee par l'activite, reconnue par intervals.icu (ICU)
  // ou par le jour (JOUR).
  idPlanifiee?: string | null;
  appariement?: 'ICU' | 'JOUR' | null;
};

export type Journal = {
  compteConfigure: boolean;
  planifiees: SeancePlanifiee[];
  activites: ActiviteRealisee[];
  // Semaines d'assimilation / d'affutage de la periode.
  semaines?: { lundi: string; type: string | null }[];
  // Plus ancienne lecture intervals.icu des mois affiches (null : jamais).
  dateSynchro?: string | null;
  // intervals.icu indisponible : activites conservees seulement.
  avertissement?: string | null;
};

/**
 * Journal de la periode : activites conservees, relues dans intervals.icu
 * seulement si besoin (mois jamais lus ou recents) ; actualiser force la
 * relecture de la periode.
 */
export async function lireJournal(debut: string, fin: string, actualiser = false, athlete?: Cible): Promise<Journal> {
  const response = await fetch(avecAthlete(`/api/journal?debut=${debut}&fin=${fin}${actualiser ? '&actualiser=true' : ''}`, athlete), {
    headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
  });
  if (response.status !== 200) {
    let message = 'statut ' + response.status;
    try {
      const erreur = await response.json();
      if (erreur?.message) message = erreur.message;
    } catch {
      // corps vide ou non JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as Journal;
}

// Sports de course a pied (allure affichee, totaux "course").
const COURSE = new Set(['Run', 'TrailRun', 'VirtualRun', 'Treadmill']);
export function estCourse(a: ActiviteRealisee): boolean {
  return !!a.type && COURSE.has(a.type);
}

// Allure moyenne en mouvement, ex "5:12 /km".
export function allureMoyenne(a: ActiviteRealisee): string {
  if (!a.distanceM || !a.dureeS || a.distanceM < 100) return '';
  const sParKm = Math.round(a.dureeS / (a.distanceM / 1000));
  return `${Math.floor(sParKm / 60)}:${String(sParKm % 60).padStart(2, '0')} /km`;
}


export type KilometrageTotal = { debut: string; fin: string; distanceM: number; nbSorties: number };
export type Kilometrage = {
  compteConfigure: boolean;
  anneePrecedente: number;
  totalAnneePrecedente: KilometrageTotal;
  total12Mois: KilometrageTotal;
  avertissement?: string | null;
};

/** Kilometrage de course : annee civile precedente et 12 derniers mois. */
export async function lireKilometrage(): Promise<Kilometrage> {
  const response = await fetch('/api/journal/kilometrage', {
    headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
  });
  if (response.status !== 200) throw new Error('statut ' + response.status);
  return (await response.json()) as Kilometrage;
}

/**
 * Telecharge l'export du journal pour Google Sheets (CSV, une ligne par
 * semaine du lundi au dimanche couvrant la periode, un an maximum).
 */
export async function exporterJournal(debut: string, fin: string): Promise<void> {
  const response = await fetch(`/api/journal/export?debut=${debut}&fin=${fin}`, {
    headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token') },
  });
  if (response.status !== 200) {
    let message = 'statut ' + response.status;
    try {
      const erreur = await response.json();
      if (erreur?.message) message = erreur.message;
    } catch {
      // corps vide ou non JSON
    }
    throw new Error(message);
  }
  const url = URL.createObjectURL(await response.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = `journal-${debut}-au-${fin}.csv`;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}
